import { forwardRef, useEffect, useRef, useState } from "react";
import { resetState, selectDitaPath, selectTocId, setGuideline } from "../counter/counterSlice";
import useAppModernisation from "../../hooks/useAppModernisation";
import classNames from "classnames";
import AcknowledgementModal from "../../components/AcknowledgementModal/acknowledgementModal";
import PdfModal from "../../components/pdfModal";
import { useLocation, useNavigationType } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EnhancedCounter } from "../../components/EnhancedCounter/EnhancedCounter";
import Counter from "../counter/Counter";
import Version from "../counter/Version";
import useContentOverflow from "../../hooks/tablesAndFiguresHooks/useContentOverflow";
import useDebouncedMutationEffect from "../../hooks/useDebouncedMutationEffect";
import ContentSideBar from "./ContentSideBar";
import styles from "./ModernMainContent.module.scss";
import { useIsPregnancyBreastfeedingPage } from "../../utils/pregnancyPageUtils";
import AbxCalcApp from "../calculator/AbxCalcApp";
import CrcCalcApp from "../calculator/CrcCalcApp";
import LbwCalcApp from "../calculator/LbwCalcApp";
import BsaCalcApp from "../calculator/BsaCalcApp";
import GfrCalcApp from "../calculator/GfrCalcApp";
import IbwCalcApp from "../calculator/IbwCalcApp";
import { useScrollActiveTocItem } from "../../hooks/tableOfContentsHooks/useScrollActiveTocItem";
import { useScrollRestoration } from "../../hooks/useScrollRestoration";
import { calculatorTitles } from "../calculator/utils";

const ModernMainContent = forwardRef(
  ({ expandExpertGroupsSideBar, expandUpdatesSideBar, scrollContainerRef }, ref) => {
    const { pathname, search } = useLocation();
    const navigationType = useNavigationType();
    const [displayGuideline, setDisplayGuideline] = useState(false);
    const [referencePopup, setReferencePopup] = useState("");
    const dispatch = useDispatch();
    const tocId = useSelector(selectTocId);
    const refInfo = useRef(null);
    const { isMobileView, isEnhancedCounter } = useAppModernisation();
    const contentOverflow = useContentOverflow();
    const [isCalculator, setIsCalculator] = useState(false);
    const [calculatorType, setCalculatorType] = useState("");

    // Check if we're on the pregnancy/breastfeeding page
    const isPregnancyPage = useIsPregnancyBreastfeedingPage(search, pathname);

    // Set up scroll listener for TOC highlighting
    const { onScroll, isTabletSize } = useScrollActiveTocItem();

    // Internal ref to the scroll container so we can restore scroll
    // position on browser back/forward navigation.
    // On mobile, AnimatedRoute mounts the hook against the page-level
    // scroll container — mounting it here too would create duplicate
    // listeners and conflicting restore attempts. Disable in mobile mode.
    const internalScrollRef = useRef(null);
    useScrollRestoration(internalScrollRef, { enabled: !isMobileView });

    useEffect(() => {
      if (navigationType === "POP") {
        dispatch(resetState());
      }
      setTimeout(() => dispatch(setGuideline({ search: search })), 100);
      if (search.indexOf("topicfile") === -1) {
        setDisplayGuideline(true);
      }
    }, [search, dispatch]);

    useDebouncedMutationEffect(ref, contentOverflow.checkContentOverflow, [], 150);

    const treeMenuClass = classNames("d-none");

    const ditapath = useSelector(selectDitaPath);

    const handleClose = () => {
      setIsCalculator(false);
    };

    return (
      <div
        ref={(node) => {
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          if (scrollContainerRef) {
            scrollContainerRef.current = node;
          }
          internalScrollRef.current = node;
        }}
        className={"topicMainContent"}
        onScroll={isTabletSize ? undefined : onScroll}
      >
        <AcknowledgementModal />
        <PdfModal />

        <div className={classNames(styles.contentPage)}>
          <div
            className={classNames(styles.mainContent, {
              appMode: isMobileView,
              [styles.noSideContent]: isPregnancyPage,
            })}
          >
            <div className={classNames("sectionBlockLayout", styles.sectionBlock)}>
              <div className="dart-container">
                <div className="maincontent">
                  <noscript>You need to enable JavaScript to run this app.</noscript>
                  <div className={`columnBlockLayout ${treeMenuClass}`} id="tocId">
                    <div id="tocbox">
                      <div id="tocID" dangerouslySetInnerHTML={{ __html: tocId }} />
                      <div id="TOC_ID">
                        {ditapath && (
                          <div id="tg_toc_id">
                            <div
                              className="dartframe fa fa-compress"
                              onClick={() => {
                                document
                                  .querySelectorAll("#TOC_ID, #contentId")
                                  .forEach((el) => el.classList.toggle("active"));
                              }}
                            ></div>
                            <Version />
                            {isEnhancedCounter ? (
                              <EnhancedCounter
                                pathname={pathname}
                                search={search}
                                setReferencePopup={setReferencePopup}
                                forwardedRef={refInfo}
                                setIsCalculator={setIsCalculator}
                                setCalculatorType={setCalculatorType}
                              />
                            ) : (
                              <Counter
                                pathname={pathname}
                                search={search}
                                setReferencePopup={setReferencePopup}
                                ref={refInfo}
                                setIsCalculator={setIsCalculator}
                                setCalculatorType={setCalculatorType}
                              />
                            )}
                            <span className="username"></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={classNames("columnBlockLayout")} id="etgcontent">
                    <div id="contentId">
                      {referencePopup}
                      <div className="bodyContentDiv">
                        <section id="sectionContent"></section>
                      </div>
                    </div>
                    <div className="reader-list">
                      <div id="etgGuidelineBox">
                        {displayGuideline && (
                          <div className="topiclist noLeftPaddingXs noRightPaddingXs boxstrnth">
                            <div className="guideline-box mar-t32"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isPregnancyPage && (
            <ContentSideBar
              ditapath={ditapath}
              expandExpertGroupsSideBar={expandExpertGroupsSideBar}
              expandUpdatesSideBar={expandUpdatesSideBar}
            />
          )}
          {isCalculator && (
            <div className={styles.calculatorSideBar}>
              <div className={styles.title}>
                <span className={styles.calculatorTitle}>
                  {["amikacin", "gentamicin", "tobramycin"].includes(calculatorType)
                    ? "Aminoglycoside initial dose calculator for adults"
                    : calculatorTitles.find(
                        (item) => item.id.toLowerCase() === calculatorType.toLowerCase(),
                      )?.title}
                </span>
                <i
                  className={classNames("tg-icons-Cross size-24", styles.closeIcon)}
                  onClick={handleClose}
                ></i>
              </div>
              {calculatorType === "ccbwcalc" && (
                <CrcCalcApp isWebLayout={false} dropdownUnit={true} data-testid="ccbw-calc" />
              )}
              {calculatorType === "ibwcalc" && (
                <IbwCalcApp isWebLayout={false} dropdownUnit={true} data-testid="ibw-calc" />
              )}
              {/* valided */}
              {calculatorType === "lbwcalc" && (
                <LbwCalcApp isWebLayout={false} dropdownUnit={true} data-testid="lbw-calc" />
              )}
              {/* valided */}
              {calculatorType === "bsacalc" && (
                <BsaCalcApp isWebLayout={false} dropdownUnit={true} data-testid="bsa-calc" />
              )}
              {/* valided */}
              {calculatorType === "gfrcalc" && (
                <GfrCalcApp isWebLayout={false} dropdownUnit={true} data-testid="bsa-calc" />
              )}
              {/* valided */}
              {["amikacin", "gentamicin", "tobramycin"].includes(calculatorType) && (
                <AbxCalcApp
                  isWebLayout={false}
                  calculatorType={calculatorType}
                  dropdownUnit={true}
                  data-testid="id-calc"
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default ModernMainContent;
